import {getBasicUserData, isIdenticalSearch, encodeSearchFilters} from '../utils/personalization';
import {getStorageItem, setStorageItem} from '../../../services/storageService';
import {IndexedSearch, SearchFilter} from '../../../services/indexDB/indexeddbServiceSearches';
import {base64ToUtf8, isBase64, utf8ToBase64} from '../../../utils/base64';

interface Blacklist {
    [index: string]: {
        searches: {
            [index: number]: SearchFilter[][];
        };
    };
}

export const BLACKLIST_KEY = 'PSearchesBlacklist';

const getUserKey = formName => {
    return Object.values({...getBasicUserData(), formName}).join('');
};

const getServerBlacklist = async () => {
    try {
        const blacklistBase64 = await getStorageItem(BLACKLIST_KEY);
        const {PSearchesBlacklist} = blacklistBase64;
        const encodePSearchesBlacklist = isBase64(PSearchesBlacklist)
            ? base64ToUtf8(PSearchesBlacklist)
            : PSearchesBlacklist;
        return PSearchesBlacklist ? JSON.parse(encodePSearchesBlacklist) : null;
    } catch (err) {
        setStorageItem(BLACKLIST_KEY, '');
        console.error('An error occured while getting Personalization searches blacklist:', err);
    }
};

export const getUserBLFromServer = async formName => {
    const blacklist = await getServerBlacklist();
    if (!blacklist) {
        return null;
    } else {
        const userKey = getUserKey(formName);
        return blacklist[userKey];
    }
};

export const isBlacklisted = (relevantSearches, searchFilters) => {
    const identicalSearch = search => {
        return isIdenticalSearch(searchFilters, search);
    };
    const identicalSearchIndex = relevantSearches.findIndex(identicalSearch);
    return identicalSearchIndex >= 0;
};

const addBlacklistEntry = async (value: Blacklist) => {
    try {
        await setStorageItem(BLACKLIST_KEY, utf8ToBase64(JSON.stringify(value)));
    } catch (err) {
        console.error('An error occured while updating Personalization Searches Blacklist', err);
    }
};

const updateBlacklist = async (blacklist: any, userKey: string, searchFilters: SearchFilter[]) => {
    const blacklistCopy = {...blacklist};
    const {searches} = blacklistCopy[userKey];
    const index = searchFilters.length;
    const relevantSearches = searches[index];

    if (!relevantSearches) {
        searches[index] = [searchFilters];
        await addBlacklistEntry(blacklistCopy);
    } else {
        const blacklisted = isBlacklisted(relevantSearches, searchFilters);
        if (!blacklisted) {
            searches[index].push(searchFilters);
            await addBlacklistEntry(blacklistCopy);
        }
    }
};

export const addSearchToServerBlacklist = async (formName: string, search: IndexedSearch) => {
    const blacklist = await getServerBlacklist();
    const {filters} = search;
    const userKey = getUserKey(formName);
    const encodedFilters = encodeSearchFilters(filters);
    if (!blacklist) {
        const data = {
            [userKey]: {searches: {[filters.length]: [encodedFilters]}}
        };
        await addBlacklistEntry(data);
    } else {
        if (!blacklist[userKey]) {
            const blacklistCopy = {...blacklist};
            blacklistCopy[userKey] = {searches: {[filters.length]: [encodedFilters]}};
            addBlacklistEntry(blacklistCopy);
        } else {
            await updateBlacklist(blacklist, userKey, encodedFilters);
        }
    }
};
